import { api } from "../api/client";

/**
 * What presenter mode reads about the room: the lot in front of it, the auction running on that
 * lot, and how many are watching.
 *
 * **The public snapshot, polled** — the route a viewer's room reads on entry and on its slow poll
 * (`public-show.ts`), so nothing new was invented to serve it and the presenter is shown exactly
 * what a viewer's room is built from. The room proper is snapshot-then-subscribe over AppSync
 * Events (arch §6.3); presenter mode is not, and that is a decision rather than an omission. The
 * room's real-time client lives in the customer application, and moving it into a shared package
 * is a change to the one part of the system that failed on the first deployed show — not
 * something to do as a side effect of a screen. A two-second poll is what it costs instead: a bid
 * reaches the presenter up to two seconds after it reaches the room, and the clock, which is
 * counted down locally from the deadline, is not late at all.
 *
 * Only the fields presenter mode draws are typed here. The payload is `publicLotJson` and
 * `auctionPayload` in `packages/core`; this is a reader of them, not a second definition.
 */

/** How often presenter mode re-reads the room while it is open. */
export const PRESENTER_POLL_MS = 2_000;

export interface PresenterLot {
  id: string;
  saleMode: "fixed" | "auction";
  /** The fixed price, or an auction's starting price. `"0.00"` for a free lot (`R-LOT-32`). */
  price: string;
  state: string;
  soldOut: boolean;
  title: string | null;
  variantTitle: string | null;
  images: { url: string; alt: string | null }[];
  /** Only for an auction lot (`R-AUC-28`), or a fixed-price one showing *X left* (`R-LOT-33`). */
  unitsRemaining: number | null;
  /** When a timed fixed-price lot's sale ends (`R-LOT-34`). Null otherwise. */
  closesAt?: string | null;
}

export interface PresenterAuction {
  lotId: string;
  round: number;
  state: "open" | "closed";
  paused: boolean;
  /** The server's instant; counted down locally (`R-VID-1`). Null while paused. */
  deadline: string | null;
  /** What is left on a paused clock. */
  remainingMs: number | null;
  highBid: string | null;
  /** Who is leading, by display name (`R-AUC-29`). */
  leaderName: string | null;
  winnerName: string | null;
}

export interface PresenterRoom {
  lots: PresenterLot[];
  auctions: Record<string, PresenterAuction>;
  /** `R-ANL-1` — the figure the room shows its viewers. */
  viewers: number;
  /** For the clock-skew correction the room makes too (`R-VID-1`). */
  serverTime: string;
}

export const presenterRoom = (publicId: string) =>
  api.get<PresenterRoom>(`/api/public/shows/${encodeURIComponent(publicId)}`);
